import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useSiteConfig } from "@/lib/siteConfig";

// Injects admin-managed third-party snippets (analytics, pixels, chat widgets,
// verification tags) into the document. Runs on public pages only — not the
// admin console or member portal. Scripts inserted via innerHTML don't execute,
// so we rebuild <script> elements manually.
function injectHTML(html, target, prepend = false) {
  if (!html || !html.trim()) return;
  const tmp = document.createElement("template");
  tmp.innerHTML = html;
  const nodes = Array.from(tmp.content.childNodes);
  (prepend ? nodes.reverse() : nodes).forEach((node) => {
    let el;
    if (node.tagName === "SCRIPT") {
      el = document.createElement("script");
      Array.from(node.attributes).forEach((a) => el.setAttribute(a.name, a.value));
      el.text = node.textContent;
    } else {
      el = node.cloneNode(true);
    }
    prepend ? target.insertBefore(el, target.firstChild) : target.appendChild(el);
  });
}

// Official Google Tag Manager snippets built from a container ID (GTM-XXXXXXX).
function gtmHead(id) {
  return `<script>(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${id}');</script>`;
}
function gtmBody(id) {
  return `<noscript><iframe src="https://www.googletagmanager.com/ns.html?id=${id}" height="0" width="0" style="display:none;visibility:hidden"></iframe></noscript>`;
}

export default function CustomScripts() {
  const location = useLocation();
  const cfg = useSiteConfig();
  const done = useRef(false);
  const isPublic = !location.pathname.startsWith("/admin") && !location.pathname.startsWith("/member");

  useEffect(() => {
    if (done.current || !isPublic) return;
    if (cfg.head_code === undefined && cfg.body_code === undefined && cfg.gtm_container_id === undefined) return; // config not loaded yet
    done.current = true;

    const gtmId = (cfg.gtm_container_id || "").trim();
    if (/^GTM-[A-Z0-9]+$/i.test(gtmId) && !window.__gtmInjected) {
      window.__gtmInjected = true;
      injectHTML(gtmHead(gtmId), document.head, true);  // GTM must load as high as possible
      injectHTML(gtmBody(gtmId), document.body, true);   // noscript immediately after <body>
    }

    injectHTML(cfg.head_code, document.head);
    injectHTML(cfg.body_code, document.body);
  }, [isPublic, cfg]);

  return null;
}
